import React, { useState } from 'react';
import { BarChart3, TrendingUp, AlertTriangle, CheckCircle, Users, Calendar } from 'lucide-react';

interface ProjectSummary {
  id: string;
  name: string;
  client: string;
  progress: number;
  health: number;
  status: 'on-track' | 'at-risk' | 'delayed' | 'completed';
  targetDate: string;
  csat: number;
  pm: string;
}

export const ProgramDashboard: React.FC = () => {
  const [projects] = useState<ProjectSummary[]>([
    {
      id: 'proj-001',
      name: 'Tyler ERP Implementation',
      client: 'Acme County',
      progress: 72,
      health: 87,
      status: 'on-track',
      targetDate: '2024-09-30',
      csat: 4.2,
      pm: 'Sarah Johnson'
    },
    {
      id: 'proj-002',
      name: 'Public Safety Suite',
      client: 'Metro City',
      progress: 45,
      health: 65,
      status: 'at-risk',
      targetDate: '2024-10-15',
      csat: 3.8,
      pm: 'Mike Chen'
    },
    {
      id: 'proj-003',
      name: 'Court Management System',
      client: 'State Judicial',
      progress: 89,
      health: 92,
      status: 'on-track',
      targetDate: '2024-08-31',
      csat: 4.5,
      pm: 'Lisa Rodriguez'
    }
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-track': return 'text-[#8A9150] bg-[#8A9150]/10 border-[#8A9150]/20';
      case 'at-risk': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'delayed': return 'text-red-600 bg-red-100 border-red-200';
      case 'completed': return 'text-blue-600 bg-blue-100 border-blue-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on-track': return <CheckCircle className="w-4 h-4 text-[#8A9150]" />;
      case 'at-risk': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'delayed': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-blue-600" />;
      default: return <BarChart3 className="w-4 h-4 text-gray-600" />;
    }
  };

  const avgProgress = Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length);
  const avgHealth = Math.round(projects.reduce((sum, p) => sum + p.health, 0) / projects.length);
  const avgCSAT = (projects.reduce((sum, p) => sum + p.csat, 0) / projects.length).toFixed(1);
  const onTimeDelivery = Math.round((projects.filter(p => p.status === 'on-track' || p.status === 'completed').length / projects.length) * 100);

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
            Program Dashboard
          </h2>
          <p className="text-gray-600 text-lg">
            Multi-project rollup with status, risk, and CSAT tracking
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <BarChart3 className="w-6 h-6 text-[#4267A1]" />
              <h3 className="text-lg font-semibold text-gray-900">Avg Progress</h3>
            </div>
            <div className="text-3xl font-bold text-[#4267A1] mb-2">{avgProgress}%</div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-[#8A9150]" />
              <span className="text-sm text-[#8A9150]">+5% this month</span>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <CheckCircle className="w-6 h-6 text-[#8A9150]" />
              <h3 className="text-lg font-semibold text-gray-900">Avg Health</h3>
            </div>
            <div className="text-3xl font-bold text-[#8A9150] mb-2">{avgHealth}</div>
            <div className="text-sm text-gray-600">Overall project health</div>
          </div>

          <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Avg CSAT</h3>
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-2">{avgCSAT}</div>
            <div className="text-sm text-gray-600">Client satisfaction</div>
          </div>

          <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Calendar className="w-6 h-6 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">On-Time</h3>
            </div>
            <div className="text-3xl font-bold text-purple-600 mb-2">{onTimeDelivery}%</div>
            <div className="text-sm text-gray-600">Delivery rate</div>
          </div>
        </div>

        <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Active Projects</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Project</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Progress</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Health</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Target Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">CSAT</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">PM</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="font-medium text-gray-900">{project.name}</div>
                      <div className="text-sm text-gray-500">{project.id}</div>
                    </td>
                    <td className="py-4 px-4 text-gray-900">{project.client}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-[#4267A1] h-2 rounded-full" 
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{project.progress}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-lg font-bold text-[#8A9150]">{project.health}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(project.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                          {project.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-900">
                      {new Date(project.targetDate).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-lg font-bold text-blue-600">{project.csat}</span>
                    </td>
                    <td className="py-4 px-4 text-gray-900">{project.pm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
